import * as React from "react";

const HAS_FORMATTING_RE = /<\/?(?:b|i|strong|em)>/i;

/** True when `text` contains supported inline formatting tags. */
export function hasInlineFormatting(text: string): boolean {
  return HAS_FORMATTING_RE.test(text);
}

function isBoldTag(tag: string): boolean {
  const name = tag.toLowerCase();
  return name === "b" || name === "strong";
}

/**
 * Parse inline `<b>`, `<i>` (and legacy `<strong>`, `<em>`) in plain segments.
 * Mention/signoff tags must be stripped before calling this.
 */
export function parseInlineFormatting(text: string, keyPrefix = "fmt"): React.ReactNode {
  if (!hasInlineFormatting(text)) {
    return text;
  }

  const parts: React.ReactNode[] = [];
  let key = 0;
  let rest = text;

  while (rest.length > 0) {
    const openMatch = rest.match(/<(b|i|strong|em)>/i);
    if (!openMatch || openMatch.index === undefined) {
      if (rest) {
        parts.push(rest);
      }
      break;
    }

    if (openMatch.index > 0) {
      parts.push(rest.slice(0, openMatch.index));
    }

    const rawTag = openMatch[1];
    const bold = isBoldTag(rawTag);
    const afterOpen = rest.slice(openMatch.index + openMatch[0].length);
    const closeRe = bold ? /<\/b>|<\/strong>/i : /<\/i>|<\/em>/i;
    const closeMatch = afterOpen.match(new RegExp(`([\\s\\S]*?)(${closeRe.source})`, "i"));

    if (!closeMatch) {
      parts.push(rest.slice(openMatch.index));
      break;
    }

    const inner = parseInlineFormatting(closeMatch[1], `${keyPrefix}-${key}`);
    parts.push(
      bold ? (
        <b key={`${keyPrefix}-${key++}`}>{inner}</b>
      ) : (
        <i key={`${keyPrefix}-${key++}`}>{inner}</i>
      ),
    );
    rest = afterOpen.slice(closeMatch[0].length);
  }

  if (parts.length === 0) {
    return text;
  }
  if (parts.length === 1) {
    return parts[0];
  }
  return <React.Fragment key={keyPrefix}>{parts}</React.Fragment>;
}
